"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { transformRoomThemeValue } from "@/src/utils/roomValueTransformations"

// Import the gallery data and types
import type { GalleryItem } from "../gallery/GalleryPage"

// Gallery data (same as in GalleryPage)
const galleryItems: GalleryItem[] = [
  {
    id: "1",
    title: "Modern Living Room",
    roomType: "living",
    style: "modern",
    image: "/images/gallery/modern-living-room.png",
    likes: 124,
    isLiked: false,
  },
  {
    id: "2",
    title: "Scandinavian Bedroom",
    roomType: "bedroom",
    style: "scandinavian",
    image: "/images/gallery/scandinavian-bedroom.png",
    likes: 89,
    isLiked: true,
  },
  {
    id: "3",
    title: "Industrial Kitchen",
    roomType: "kitchen",
    style: "industrial",
    image: "/images/gallery/industrial-kitchen.png",
    likes: 156,
    isLiked: false,
  },
  {
    id: "4",
    title: "Farmhouse Dining",
    roomType: "dining",
    style: "farmhouse",
    image: "/images/gallery/farmhouse-dining.png",
    likes: 203,
    isLiked: true,
  },
  {
    id: "5",
    title: "Mid-Century Bathroom",
    roomType: "bathroom",
    style: "midcentury",
    image: "/images/gallery/midcentury-bathroom.png",
    likes: 67,
    isLiked: false,
  },
  {
    id: "6",
    title: "Bohemian Office",
    roomType: "office",
    style: "bohemian",
    image: "/images/gallery/bohemian-office.png",
    likes: 98,
    isLiked: false,
  },
  {
    id: "7",
    title: "Coastal Bedroom",
    roomType: "bedroom",
    style: "coastal",
    image: "/images/gallery/coastal-bedroom.png",
    likes: 142,
    isLiked: true,
  },
  {
    id: "8",
    title: "Traditional Living Room",
    roomType: "living",
    style: "traditional",
    image: "/images/gallery/traditional-living-room.png",
    likes: 178,
    isLiked: false,
  },
  {
    id: "9",
    title: "Modern Kitchen",
    roomType: "kitchen",
    style: "modern",
    image: "/images/gallery/modern-kitchen.png",
    likes: 234,
    isLiked: true,
  },
  {
    id: "10",
    title: "Scandinavian Living Room",
    roomType: "living",
    style: "scandinavian",
    image: "/images/gallery/scandinavian-living-room.png",
    likes: 167,
    isLiked: false,
  },
  {
    id: "11",
    title: "Industrial Bedroom",
    roomType: "bedroom",
    style: "industrial",
    image: "/images/gallery/industrial-bedroom.png",
    likes: 91,
    isLiked: false,
  },
  {
    id: "12",
    title: "Farmhouse Kitchen",
    roomType: "kitchen",
    style: "farmhouse",
    image: "/images/gallery/farmhouse-kitchen.png",
    likes: 189,
    isLiked: true,
  },
  {
    id: "13",
    title: "Modern Nursery",
    roomType: "nursery",
    style: "scandinavian",
    image: "/images/gallery/scandinavian-nursery.png",
    likes: 145,
    isLiked: false,
  },
  {
    id: "14",
    title: "Minimalist Living Room",
    roomType: "living",
    style: "minimalist",
    image: "/images/gallery/minimalist-living-room.png",
    likes: 176,
    isLiked: false,
  },
  {
    id: "15",
    title: "Rustic Bedroom",
    roomType: "bedroom",
    style: "rustic",
    image: "/images/gallery/rustic-bedroom.png",
    likes: 132,
    isLiked: true,
  },
  {
    id: "16",
    title: "Shabby Chic Dining Room",
    roomType: "dining",
    style: "shabby-chic",
    image: "/images/gallery/shabby-chic-dining.png",
    likes: 98,
    isLiked: false,
  },
  {
    id: "17",
    title: "Cottage Core Kitchen",
    roomType: "kitchen",
    style: "cottage-core",
    image: "/images/gallery/cottage-core-kitchen.png",
    likes: 154,
    isLiked: true,
  },
  {
    id: "18",
    title: "Mountain Chalet Living Room",
    roomType: "living",
    style: "mountain-chalet",
    image: "/images/gallery/mountain-chalet-living.png",
    likes: 187,
    isLiked: false,
  },
]

// Function to get random items from gallery
function getRandomGalleryItems(count = 8): GalleryItem[] {
  const shuffled = [...galleryItems].sort(() => 0.5 - Math.random())
  return shuffled.slice(0, count)
}

export function GalleryCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [displayItems, setDisplayItems] = useState<GalleryItem[]>([])

  // Initialize with random items on component mount
  useEffect(() => {
    setDisplayItems(getRandomGalleryItems(8))
  }, [])

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % Math.max(1, displayItems.length - 2))
  }

  const prevSlide = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + Math.max(1, displayItems.length - 2)) % Math.max(1, displayItems.length - 2),
    )
  }

  if (displayItems.length === 0) {
    return <div>Loading...</div>
  }

  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 33.333}%)` }}
        >
          {displayItems.map((item) => (
            <div key={item.id} className="w-1/3 flex-shrink-0 px-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="relative h-48">
                  <Image src={item.image || "/placeholder.svg"} alt={item.title} fill className="object-cover" />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm capitalize">{transformRoomThemeValue(item.style)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      <Button
        variant="outline"
        size="icon"
        className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white shadow-lg"
        onClick={prevSlide}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Button
        variant="outline"
        size="icon"
        className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white shadow-lg"
        onClick={nextSlide}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>

      {/* Dots indicator */}
      <div className="flex justify-center mt-6 space-x-2">
        {Array.from({ length: Math.max(1, displayItems.length - 2) }).map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? "bg-[#40A1AB]" : "bg-gray-300"
            }`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  )
}
